import React, { Fragment, ReactNode } from "react";

import { Caption, CaptionProps } from "@multiweb/reactComponents";
import { bridge } from "@multiweb/reactCore/bridge";
import { createLoadLocalizationKeysAction } from "@multiweb/reactCore/i18n";
import { createCreateFetchingKey } from "@multiweb/reactUtils";
import { DataConverter } from "Global/Base/Converters/DataConverter";
import { AnyConstructor, Mixin } from "Global/Base/types";
import { inputIdFromId } from "../../../utils";
import { IItemRenderer } from "../types";

const { dispatchAction } = bridge;

const createFetchingKey = createCreateFetchingKey("hasCaption");

/**
 * Mixin, který přidává Caption GeneralGridLayoutRendereru.
 *
 * @param Base Base třída rozšiřující GeneralGridLayoutItemRenderer.
 */
export const HasCaption = <T extends AnyConstructor<IItemRenderer>>(Base: T): T =>
    class HasCaption extends Base {
        public readonly _ignoreCaption: boolean;
        public readonly _captionLocKey: string;
        public readonly _captionRequired: boolean;
        public readonly _alwaysShowCaption: boolean;
        private _innerId: string;

        /**
         * Vrací hodnotu boolean. `True` pokud komponenta má caption, `False` jinak.
         */
        public get hasCaption(): boolean {
            return !this._ignoreCaption && this._captionLocKey && this._captionLocKey.toLocaleLowerCase() !== "xg-666";
        }

        public get captionProps(): CaptionProps {
            return {
                locKey: this._captionLocKey,
                required: this._captionRequired,
                alwaysShowCaption: this._alwaysShowCaption,
                htmlFor: this._innerId,
            };
        }

        constructor(...params: any[]) {
            super(...params);

            this._captionLocKey = this.innerElement.getAttribute("data-captionlockey");
            this._ignoreCaption = DataConverter.toBoolean(this.innerElement.getAttribute("data-ignorecaption"), false);
            this._captionRequired = DataConverter.toBoolean(this.innerElement.getAttribute("data-required"));
            this._alwaysShowCaption = DataConverter.toBoolean(this.innerElement.getAttribute("data-alwaysshowcaption"), true);
            this._innerId = inputIdFromId(this.innerElement.id);

            if (this.hasCaption) {
                dispatchAction(createLoadLocalizationKeysAction([this._captionLocKey], createFetchingKey(this.element.id), true));
            }
        }

        public override getMountedInnerElement(): ReactNode {
            if (!this.hasCaption) {
                return super.getMountedInnerElement();
            }
            return (
                <Fragment>
                    <Caption {...this.captionProps} />
                    {super.getMountedInnerElement()}
                </Fragment>
            );
        }
    };

export type HasLabel = Mixin<typeof HasCaption>;
